import * as React from "react";
import { createBrowserRouter, Navigate, useSearchParams } from "react-router";
import { AuthGate } from "@/components/layout/AuthGate";
import { AppShell } from "@/components/layout/AppShell";
import { SiteOwnerRedirect } from "@/components/layout/SiteOwnerRedirect";
import { DashboardRouter } from "@/components/layout/DashboardRouter";
import { ChunkErrorElement } from "@/components/layout/ChunkErrorElement";
// MV2-985 — forked: restores the pre-MV2-865 segmented pill tab strip
// look (as 4 columns, not the old 3 that dropped Notifications from
// mobile nav). See src/mobile/components/layout/SettingsShell.tsx.
import { SettingsShell } from "@/mobile/components/layout/SettingsShell";
import { LoginPage } from "@/pages/LoginPage";
import { AcceptInvitePage } from "@/pages/AcceptInvitePage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";

/**
 * MV2-XXXX — mobile-only router, isolated from `src/router.tsx`.
 *
 * Phase 1 (scaffold): every route below still points at the same shared
 * `src/pages/*` / `src/components/*` files web uses — behaviour is
 * unchanged, this is a mechanical wiring change only. That is deliberate:
 * this file is the seam. Each entry gets swapped, one screen at a time, to
 * import from `src/mobile/pages/*` / `src/mobile/components/*` instead —
 * without ever touching `src/router.tsx` or a route web relies on.
 *
 * Scope is intentionally smaller than web's router: Admin, MLOps,
 * Simulator and Quote are not part of the mobile persona surface (see
 * MobileBottomNav.tsx / nav.ts) and are not registered here at all.
 * Every site-detail tab is now forked (MV2-985) — each got its own
 * genuinely separate mobile page (pure-data hooks shared, JSX never
 * shared beyond design-system primitives, already self-adaptive
 * leaves, or widgets — like Strings' `ActionPanel`, Grid code's
 * `SignalHealth`/`DayStrip`, Controls' `ControlDeviceList`/
 * `TopForecastBand`, BESS' `BatteryConfigPanel`/`OptimizerSettingsSubTab`,
 * or Connectivity's `LoggerLane`/`HistoryCard` — that simply carry no
 * platform-conditional rendering, or take a caller-fixed `isMobile`
 * instead of reading `useViewport()` themselves) instead of staying
 * excluded.
 */

// ─── Lazy page components — Phase 1: same source as router.tsx ───────────────
const InboxPage = React.lazy(() =>
  import("@/pages/InboxPage").then((m) => ({ default: m.InboxPage })),
);
// MV2-985 — forked: restores the pre-MV2-878 mobile layout (edge-to-edge
// tab strip, full-width search row, sticky day headers, full-page scroll)
// and grafts on MV2-878's chip counts, dynamic header total, and a
// proactive site-filter picker. See src/mobile/pages/NotificationsPage.tsx.
const NotificationsPage = React.lazy(() =>
  import("@/mobile/pages/NotificationsPage").then((m) => ({
    default: m.NotificationsPage,
  })),
);
// MV2-985 — forked: the fleet Overview (every persona's landing page on
// a phone) reaches its own mobile page here in place of the shared
// `OverviewPage.tsx`. `TodayBySource`/`ActiveAlarmsCard`/`Banner` are
// shared as-is; the glance strip becomes `MobileStatCard`s and the
// seven-column `SitesTable` becomes a card per site. See
// src/mobile/pages/MobileFleetOverviewPage.tsx.
const MobileFleetOverviewPage = React.lazy(() =>
  import("@/mobile/pages/MobileFleetOverviewPage").then((m) => ({
    default: m.MobileFleetOverviewPage,
  })),
);
// MV2-985 — forked: relocates the still-current mobile map chrome
// (FilterRail/MapSearchInput/MobileMapTopBar survived MV2-874 largely
// intact) and drops Focus/TV mode, a NOC-wall feature with no real mobile
// entry point. See src/mobile/pages/MapViewPage.tsx.
const MapViewPage = React.lazy(() =>
  import("@/mobile/pages/MapViewPage").then((m) => ({ default: m.MapViewPage })),
);
const MyPlantsGridPage = React.lazy(() =>
  import("@/pages/MyPlantsGridPage").then((m) => ({
    default: m.MyPlantsGridPage,
  })),
);
const MyPlantRedirect = React.lazy(() =>
  import("@/pages/MyPlantsGridPage").then((m) => ({
    default: m.MyPlantRedirect,
  })),
);
const MyPlantPage = React.lazy(() =>
  import("@/pages/MyPlantPage").then((m) => ({ default: m.MyPlantPage })),
);
// MV2-985 — forked: Sites never had a mobile card layout even
// pre-redesign, so this is a new build (see src/mobile/pages/SitesListPage.tsx).
const SitesListPage = React.lazy(() =>
  import("@/mobile/pages/SitesListPage").then((m) => ({ default: m.SitesListPage })),
);
const FleetDevicesPage = React.lazy(() =>
  import("@/pages/FleetDevicesPage").then((m) => ({
    default: m.FleetDevicesPage,
  })),
);
const SiteDetailShell = React.lazy(() =>
  import("@/components/site/SiteDetailShell").then((m) => ({
    default: m.SiteDetailShell,
  })),
);
const MobileOverviewPage = React.lazy(() =>
  import("@/mobile/pages/site/overview/MobileOverviewPage").then((m) => ({
    default: m.MobileOverviewPage,
  })),
);
const SiteAnalyticsTab = React.lazy(() =>
  import("@/pages/site/SiteAnalyticsTab").then((m) => ({
    default: m.SiteAnalyticsTab,
  })),
);
const MobileDevicesPage = React.lazy(() =>
  import("@/mobile/pages/site/devices/MobileDevicesPage").then((m) => ({
    default: m.MobileDevicesPage,
  })),
);
const SiteDevicePage = React.lazy(() =>
  import("@/pages/site/SiteDevicePage").then((m) => ({
    default: m.SiteDevicePage,
  })),
);
const MobileAlarmsPage = React.lazy(() =>
  import("@/mobile/pages/site/alarms/MobileAlarmsPage").then((m) => ({
    default: m.MobileAlarmsPage,
  })),
);
const MobileTicketsPage = React.lazy(() =>
  import("@/mobile/pages/site/tickets/MobileTicketsPage").then((m) => ({
    default: m.MobileTicketsPage,
  })),
);
const SiteReportsTab = React.lazy(() =>
  import("@/pages/site/SiteReportsTab").then((m) => ({
    default: m.SiteReportsTab,
  })),
);
// MV2-985 — forked: the site Files tab reaches mobile for the first
// time here. `FileBrowser`/`SearchPanel` already carry their own
// self-adaptive row actions (MV2-167); only the sidebar/content split
// and the glance strip's scroll wrapper move to a real page. See
// src/mobile/pages/site/files/MobileFilesPage.tsx.
const MobileFilesPage = React.lazy(() =>
  import("@/mobile/pages/site/files/MobileFilesPage").then((m) => ({
    default: m.MobileFilesPage,
  })),
);
// MV2-985 — forked: the site Settings tab reaches mobile for the
// first time here. The five section forms (`FactsSection`, etc.) are
// shared verbatim from `pages/site/settings/SettingsSections`; only
// the glance strip and the step-list nav become a real page. See
// src/mobile/pages/site/settings/MobileSettingsPage.tsx.
const MobileSettingsPage = React.lazy(() =>
  import("@/mobile/pages/site/settings/MobileSettingsPage").then((m) => ({
    default: m.MobileSettingsPage,
  })),
);
// MV2-985 — forked: the site Financials tab reaches mobile for the
// first time here. `BillStoryHero`/`SavingsBreakdownSection`/
// `ValueHeatmapCard` are shared as-is; the glance strip and the
// battery-value card become `MobileStatCard`s instead of a
// `GlanceStrip`. See src/mobile/pages/site/financials/MobileFinancialsPage.tsx.
const MobileFinancialsPage = React.lazy(() =>
  import("@/mobile/pages/site/financials/MobileFinancialsPage").then((m) => ({
    default: m.MobileFinancialsPage,
  })),
);
// MV2-985 — forked: the site Tariff tab reaches mobile for the first
// time here. `TariffTodayForwardChart`/`TariffHistoricalView` are
// shared as-is; the glance strip becomes `MobileStatCard`s and the
// hero's stat row becomes a 2-column grid instead of desktop's
// border-divided flex row. See src/mobile/pages/site/tariff/MobileTariffPage.tsx.
const MobileTariffPage = React.lazy(() =>
  import("@/mobile/pages/site/tariff/MobileTariffPage").then((m) => ({
    default: m.MobileTariffPage,
  })),
);
// MV2-985 — forked: the site Strings tab reaches mobile for the
// first time here. `StringsGridCard`/`ActionPanel` are shared as-is
// (no platform-conditional rendering in either); the composition is
// what genuinely differs — a bottom sheet for `StringDetailPanel`
// instead of desktop's persistent right rail. See
// src/mobile/pages/site/strings/MobileStringsPage.tsx.
const MobileStringsPage = React.lazy(() =>
  import("@/mobile/pages/site/strings/MobileStringsPage").then((m) => ({
    default: m.MobileStringsPage,
  })),
);
// MV2-985 — forked: the site Grid code tab reaches mobile for the
// first time here. `SignalHealth`/`DayStrip`/`HowCalculated`/
// `EtaFunnel`/`SensorTrust`/the day drill-down are shared as-is; the
// glance strip becomes `MobileStatCard`s and the Days table becomes
// a card list, same "table too wide for a phone" fix as Files/
// Strings. See src/mobile/pages/site/gridcode/MobileGridCodeTab.tsx.
const MobileGridCodeTab = React.lazy(() =>
  import("@/mobile/pages/site/gridcode/MobileGridCodeTab").then((m) => ({
    default: m.MobileGridCodeTab,
  })),
);
// MV2-985 — forked: the site Controls tab reaches mobile for the first
// time here. `ControlDeviceList`/`TopForecastBand`/`CommandDetailDrawer`
// are shared as-is — each already forks on `useViewport()` internally or
// is already a full-width overlay on a phone; only the glance strip and
// the toolbar (search/chips/datalogger/density) become `MobileStatCard`s
// and a search row + filter bottom sheet. See
// src/mobile/pages/site/controls/MobileControlsPage.tsx. The per-device
// editor (`controls/devices/:deviceId`) is reused unforked — its rail
// already collapses to a back link under `lg:`.
const MobileControlsPage = React.lazy(() =>
  import("@/mobile/pages/site/controls/MobileControlsPage").then((m) => ({
    default: m.MobileControlsPage,
  })),
);
const SiteControlDevicePage = React.lazy(
  () => import("@/pages/site/SiteControlDevicePage"),
);
// MV2-985 — forked: the site BESS tab reaches mobile for the first time
// here. `BatteryConfigPanel`/`OptimizerSettingsSubTab` are reused as-is,
// passed a fixed `isMobile` — both already fork internally into
// genuinely separate JSX (`MobileBESSListView`/`MobileBESSEditorView`/
// `MobileBESSEditorSheet` vs their desktop list/editor) from the
// original MV2-884 canvas build; only the glance strip becomes
// `MobileStatCard`s. See src/mobile/pages/site/bess/MobileBESSPage.tsx.
const MobileBESSPage = React.lazy(() =>
  import("@/mobile/pages/site/bess/MobileBESSPage").then((m) => ({
    default: m.MobileBESSPage,
  })),
);
// MV2-985 — forked: the site Connectivity tab reaches mobile for the
// first time here. `LoggerLane`/`HistoryCard` are reused as-is, passed
// a fixed `isMobile` — see their own doc comments for why that's safe.
// Desktop's ≥4-logger verdict-card compare grid has no mobile
// equivalent: this page always stacks every lane worst-first instead.
// See src/mobile/pages/site/connectivity/MobileConnectivityPage.tsx.
const MobileConnectivityPage = React.lazy(() =>
  import("@/mobile/pages/site/connectivity/MobileConnectivityPage").then((m) => ({
    default: m.MobileConnectivityPage,
  })),
);
// MV2-985 — forked: restores the pre-MV2-872 mobile card-list layout.
// See src/mobile/pages/AlarmsListPage.tsx for why.
const AlarmsListPage = React.lazy(() =>
  import("@/mobile/pages/AlarmsListPage").then((m) => ({ default: m.AlarmsListPage })),
);
// MV2-985 — forked: `/alarms/:alarmId` (what an alarm push notification
// deep-links into) pointed at the shared desktop-only page, a fixed
// two-column layout that never collapses on a phone. Reuses AlarmDetail's
// already-mobile-native `renderAs="panel"` mode — the same thing the
// AlarmsListPage detail sheet uses — plus a back button, which that mode
// never needed until now. See src/mobile/pages/AlarmDetailPage.tsx.
const AlarmDetailPage = React.lazy(() =>
  import("@/mobile/pages/AlarmDetailPage").then((m) => ({
    default: m.AlarmDetailPage,
  })),
);
// MV2-985 — forked: restores the pre-MV2-875 mobile card-list layout.
// See src/mobile/pages/TicketsListPage.tsx for why.
const TicketsListPage = React.lazy(() =>
  import("@/mobile/pages/TicketsListPage").then((m) => ({
    default: m.TicketsListPage,
  })),
);
// MV2-985 — forked: relocates the already-current MobileTicketDetail (web
// never lost this one). See src/mobile/pages/TicketDetailPage.tsx.
const TicketDetailPage = React.lazy(() =>
  import("@/mobile/pages/TicketDetailPage").then((m) => ({
    default: m.TicketDetailPage,
  })),
);
// MV2-985 — forked: relocates the mobile-only views MV2-877 never touched
// (the web page's own comment says "Mobile keeps its own views"). See
// src/mobile/pages/ReportsPage.tsx.
const ReportsPage = React.lazy(() =>
  import("@/mobile/pages/ReportsPage").then((m) => ({ default: m.ReportsPage })),
);
const SettingsMyProfilePage = React.lazy(() =>
  import("@/pages/SettingsMyProfilePage").then((m) => ({
    default: m.SettingsMyProfilePage,
  })),
);
const SettingsNotificationsPage = React.lazy(() =>
  import("@/pages/SettingsNotificationsPage").then((m) => ({
    default: m.SettingsNotificationsPage,
  })),
);
const SettingsSecurityPage = React.lazy(() =>
  import("@/pages/SettingsSecurityPage").then((m) => ({
    default: m.SettingsSecurityPage,
  })),
);
// MV2-985 — SettingsShell's own nav (and SettingsIndex's four cards) has
// always listed "Access" fourth alongside Profile/Notifications/Security,
// and the page itself is already useViewport()-aware like its three
// siblings — but this route was never added here, so tapping it fell
// through to the `*` catch-all and silently bounced to the dashboard.
// Reused unforked, same as the other three Settings pages.
const SettingsInvitationsPage = React.lazy(() =>
  import("@/pages/SettingsInvitationsPage").then((m) => ({
    default: m.SettingsInvitationsPage,
  })),
);

// ─── Page-level Suspense skeleton — matches router.tsx's PageSkeleton ─────────
function PageSkeleton(): React.ReactElement {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-[color:var(--brand-primary)] border-t-transparent" />
    </div>
  );
}

/** MV2-848: `/sites` is the list; `/sites?view=devices` is the fleet device timeline. */
function SitesIndex(): React.ReactElement {
  const [params] = useSearchParams();
  return params.get("view") === "devices" ? (
    <FleetDevicesPage />
  ) : (
    <SitesListPage />
  );
}

export const mobileRouter = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/accept-invite/:token", element: <AcceptInvitePage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  {
    element: <AuthGate />,
    errorElement: <ChunkErrorElement />,
    children: [
      {
        path: "/",
        element: <AppShell />,
        children: [
          { index: true, element: <DashboardRouter /> },
          {
            path: "inbox",
            element: (
              <React.Suspense fallback={<PageSkeleton />}>
                <InboxPage />
              </React.Suspense>
            ),
          },
          {
            path: "notifications",
            element: (
              <React.Suspense fallback={<PageSkeleton />}>
                <NotificationsPage />
              </React.Suspense>
            ),
          },
          {
            path: "overview",
            element: (
              <SiteOwnerRedirect>
                <React.Suspense fallback={<PageSkeleton />}>
                  <MobileFleetOverviewPage />
                </React.Suspense>
              </SiteOwnerRedirect>
            ),
          },
          {
            path: "map",
            element: (
              <SiteOwnerRedirect>
                <React.Suspense fallback={<PageSkeleton />}>
                  <MapViewPage />
                </React.Suspense>
              </SiteOwnerRedirect>
            ),
          },
          {
            path: "my-plants",
            element: (
              <React.Suspense fallback={<PageSkeleton />}>
                <MyPlantsGridPage />
              </React.Suspense>
            ),
          },
          {
            path: "my-plant",
            element: (
              <React.Suspense fallback={<PageSkeleton />}>
                <MyPlantRedirect />
              </React.Suspense>
            ),
          },
          {
            path: "my-plant/:siteId",
            element: (
              <React.Suspense fallback={<PageSkeleton />}>
                <MyPlantPage />
              </React.Suspense>
            ),
          },
          {
            path: "sites",
            children: [
              {
                index: true,
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <SitesIndex />
                  </React.Suspense>
                ),
              },
              {
                path: ":siteId",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <SiteDetailShell />
                  </React.Suspense>
                ),
                children: [
                  { index: true, element: <Navigate to="overview" replace /> },
                  {
                    path: "overview",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <MobileOverviewPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "analytics",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteAnalyticsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "devices",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <MobileDevicesPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "devices/:deviceId/:view?",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteDevicePage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "alarms",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <MobileAlarmsPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "tickets",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <MobileTicketsPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "reports",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteReportsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "files",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <MobileFilesPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "settings",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <MobileSettingsPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "financials",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <MobileFinancialsPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "tariff",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <MobileTariffPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "strings",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <MobileStringsPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "gridcode",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <MobileGridCodeTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "controls",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <MobileControlsPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "controls/devices/:deviceId",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteControlDevicePage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "bess",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <MobileBESSPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "connectivity",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <MobileConnectivityPage />
                      </React.Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: "alarms",
            element: (
              <SiteOwnerRedirect>
                <React.Suspense fallback={<PageSkeleton />}>
                  <AlarmsListPage />
                </React.Suspense>
              </SiteOwnerRedirect>
            ),
          },
          {
            path: "alarms/:alarmId",
            element: (
              <SiteOwnerRedirect>
                <React.Suspense fallback={<PageSkeleton />}>
                  <AlarmDetailPage />
                </React.Suspense>
              </SiteOwnerRedirect>
            ),
          },
          {
            path: "tickets",
            element: (
              <SiteOwnerRedirect>
                <React.Suspense fallback={<PageSkeleton />}>
                  <TicketsListPage />
                </React.Suspense>
              </SiteOwnerRedirect>
            ),
          },
          {
            path: "tickets/:id",
            element: (
              <SiteOwnerRedirect>
                <React.Suspense fallback={<PageSkeleton />}>
                  <TicketDetailPage />
                </React.Suspense>
              </SiteOwnerRedirect>
            ),
          },
          {
            path: "reports",
            element: (
              <React.Suspense fallback={<PageSkeleton />}>
                <ReportsPage />
              </React.Suspense>
            ),
          },
          // Fleet Analytics left the product (25 Sep); old links land on the Overview.
          { path: "analytics", element: <Navigate to="/overview" replace /> },
          {
            path: "settings",
            element: <SettingsShell />,
            children: [
              {
                index: true,
                element: <Navigate to="/settings/my-profile" replace />,
              },
              {
                path: "my-profile",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <SettingsMyProfilePage />
                  </React.Suspense>
                ),
              },
              {
                path: "notifications",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <SettingsNotificationsPage />
                  </React.Suspense>
                ),
              },
              {
                path: "security",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <SettingsSecurityPage />
                  </React.Suspense>
                ),
              },
              {
                path: "invitations",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <SettingsInvitationsPage />
                  </React.Suspense>
                ),
              },
              {
                path: "profile",
                element: <Navigate to="/settings/my-profile" replace />,
              },
            ],
          },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);
